import { Link, useNavigate, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { useEffect } from "react";

import { useT } from "../../i18n/I18nContext";
import { logger } from "../../util/logger";
import ErrorIllustration from "./ErrorIllustration";

// Generic error page.
//
// Two ways in:
//   * As ``errorElement`` on a router route — React Router calls
//     us when a render or loader throws and ``useRouteError()``
//     surfaces the thrown value.
//   * Direct render with props (e.g., a fatal API call decides
//     to bail) — ``status`` / ``title`` / ``body`` override the
//     defaults.
//
// Visual chrome shares ``ErrorIllustration`` with NotFound so the
// "something broke" surfaces read as one design language. Sits on
// a centred card with the app's surface/border tokens so the page
// follows light + dark themes without bespoke styling.
export default function ErrorPage({ status, title, body }) {
  const t = useT();
  const navigate = useNavigate();
  const routeError = useRouteError();

  // Prefer explicit props, then router-supplied error, then a
  // generic fallback. ``isRouteErrorResponse`` covers `throw new
  // Response(...)` and 404s from the router itself.
  let resolvedStatus = status;
  let resolvedMessage = "";
  if (!resolvedStatus && routeError) {
    if (isRouteErrorResponse(routeError)) {
      resolvedStatus = routeError.status;
      resolvedMessage = routeError.statusText || "";
    } else if (routeError instanceof Error) {
      resolvedMessage = routeError.message;
    }
  }
  resolvedStatus = resolvedStatus || 500;

  useEffect(() => {
    if (routeError) {
      logger.error("error_page_route_error", {
        status: resolvedStatus,
        message: resolvedMessage,
      });
    }
  }, [routeError, resolvedStatus, resolvedMessage]);

  const heading = title || t("error_page.title");
  const description = body || t("error_page.body");

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-text px-4 sm:px-6 lg:px-12 py-12">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface shadow-sm px-6 py-10 sm:px-10 sm:py-12 text-center">
        <ErrorIllustration
          variant="error"
          className="mx-auto h-44 w-auto mb-6"
        />
        <p className="text-xs uppercase tracking-[0.14em] text-secondText mb-2 tabular-nums">
          {t("error_page.code_label")} {resolvedStatus}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold mb-3 break-words">
          {heading}
        </h1>
        <p className="text-secondText mb-6 break-words">{description}</p>
        {resolvedMessage ? (
          <p className="app-mono text-xs text-secondText/80 mb-6 break-words bg-surfaceAlt/60 border border-border rounded-lg px-3 py-2 text-left">
            {resolvedMessage}
          </p>
        ) : null}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(0)}
            className="w-full sm:w-auto inline-flex items-center justify-center rounded-lg border border-border px-5 py-2 text-sm font-semibold text-text hover:border-primary hover:text-primary transition"
          >
            {t("error_page.retry")}
          </button>
          <Link
            to="/"
            className="w-full sm:w-auto app-button-primary inline-flex items-center justify-center"
          >
            {t("error_page.go_home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
